"use client";

import React, { useRef, useEffect, useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faCheck } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { isDatePickerInteractionTarget } from "@/components/ui/date-picker";
import clsx from "clsx";

export type SelectOption<T = string> = {
  value: T;
  label: string;
};

const DROPDOWN_MAX_WIDTH_PX = 448; // matches common max-w-md panels

/** Light popover shadow — avoid full-opacity `shadow-slate-900` on white panels. */
const DROPDOWN_PANEL_SURFACE_CLASS =
  "shadow-lg shadow-slate-900/10 ring-1 ring-slate-200/80 dark:shadow-xl dark:shadow-black/30 dark:ring-white/10";

/** Match ``FormInput`` label typography in light and dark mode. */
const SELECT_LABEL_CLASS = clsx(
  "text-xs uppercase tracking-widest font-mono",
  "text-slate-800 dark:text-slate-300",
);

const LISTBOX_OPTION_CLASS = clsx(
  "group relative cursor-pointer rounded-lg select-none",
  "text-slate-900 dark:text-slate-200",
  "data-selected:bg-indigo-50 data-selected:text-slate-900",
  "dark:data-selected:bg-slate-700 dark:data-selected:text-slate-100",
  "data-focus:bg-indigo-100 data-focus:text-slate-900",
  "dark:data-focus:bg-slate-600 dark:data-focus:text-white",
  "data-focus:outline-hidden",
);

const LISTBOX_PANEL_CLASS = clsx(
  "rounded-xl bg-white p-2 text-base text-slate-900",
  "dark:bg-slate-800 dark:text-slate-200",
  DROPDOWN_PANEL_SURFACE_CLASS,
  "focus:outline-hidden data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0",
);

/** Match ``FormInput`` field surfaces (light + dark). */
const SELECT_BUTTON_SURFACE_CLASS = clsx(
  "relative w-full cursor-pointer rounded-xl border py-1.5 pr-10 pl-3 h-10 text-left outline-none",
  "bg-white text-slate-900 border-slate-200",
  "dark:bg-slate-900/80 dark:text-slate-200 dark:border-slate-600",
  "disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-400",
  "dark:disabled:border-slate-700 dark:disabled:bg-slate-800/60 dark:disabled:text-slate-500",
  "focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-white",
  "dark:focus:ring-offset-slate-700 dark:focus:ring-indigo-400",
);

/** Listbox keyboard handlers must not run while typing in nested fields. */
function isEditableListboxTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return true;
  }
  return target.isContentEditable;
}

function handleListboxEditableKeyDown(event: React.KeyboardEvent) {
  if (!isEditableListboxTarget(event.target)) return;

  // Headless UI Listbox treats Space like Enter (select focused option).
  // stopPropagation alone does not block other listeners on the same element.
  if (event.key === " " || event.key === "Enter") {
    if (event.key === "Enter") {
      event.preventDefault();
    }
    event.stopPropagation();
  }
}

/** Block listbox Space/Enter selection while inline fields are active. */
function blockListboxSelectionKeys(event: KeyboardEvent) {
  if (event.key !== " " && event.key !== "Enter") return;
  if (isEditableListboxTarget(event.target)) return;

  event.preventDefault();
  event.stopImmediatePropagation();
}

/** Intersect viewport with horizontal clip rects from overflow-x ancestors (e.g. Mission Control shell). */
function getHorizontalClipBounds(el: HTMLElement): { left: number; right: number } {
  const pad = 4;
  let left = 0;
  let right = window.innerWidth;
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const ox = window.getComputedStyle(cur).overflowX;
    if (ox === "hidden" || ox === "clip" || ox === "auto" || ox === "scroll") {
      const r = cur.getBoundingClientRect();
      left = Math.max(left, r.left);
      right = Math.min(right, r.right);
    }
    cur = cur.parentElement;
  }
  return { left: left + pad, right: right - pad };
}

function estimateMenuWidthPx<T>(
  options: SelectOption<T>[],
  buttonWidth: number,
): number {
  const longestLabel = options.reduce(
    (m, o) => Math.max(m, (o.label ?? "").length),
    0
  );
  const fromLabels = longestLabel * 8 + 80;
  return Math.min(
    DROPDOWN_MAX_WIDTH_PX,
    Math.max(buttonWidth, fromLabels, options.length > 0 ? 200 : buttonWidth)
  );
}

interface SelectDropdownProps<T> {
  options: SelectOption<T>[];
  value: T;
  color?: "indigo";
  onChange: (value: T) => void;
  label?: string;
  className?: string;
  buttonClassName?: string;
  buttonContainerClassName?: string;
  inline?: boolean;
  renderSelectedItem?: (option: SelectOption<T>) => React.ReactNode;
  renderListHeader?: () => React.ReactNode;
  renderListItem?: (
    option: SelectOption<T>,
    isSelected: boolean,
    isFocused: boolean
  ) => React.ReactNode;
  /** Non-interactive content rendered above an option (e.g. section headings). */
  renderBeforeOption?: (
    option: SelectOption<T>,
    index: number,
    options: SelectOption<T>[],
  ) => React.ReactNode;
  renderListFooter?: () => React.ReactNode;
  dropdownClassName?: string;
  dropdownWidth?: string;
  /** When true, sizes the trigger and menu to fit option labels (no truncation). */
  fitContent?: boolean;
  disabled?: boolean;
  /** When true (default), menu renders in a portal so it is not clipped by overflow scroll areas. */
  usePortal?: boolean;
  /** Function to determine if dropdown should stay open after selecting a value */
  keepOpenOnSelect?: (value: T) => boolean;
  /** While true, Space/Enter must not activate the focused listbox option */
  suspendListboxKeyboard?: boolean;
  /** Per-option disabled state (e.g. while inline editing a row) */
  isOptionDisabled?: (value: T) => boolean;
  /** Test ID for the dropdown button */
  "data-testid"?: string;
}

export default function SelectDropdown<T>({
  options,
  value,
  onChange,
  label,
  color: _color = "indigo",
  className = "",
  buttonClassName = "",
  buttonContainerClassName,
  inline = false,
  renderSelectedItem,
  renderListHeader,
  renderListItem,
  renderBeforeOption,
  renderListFooter,
  dropdownClassName = "",
  dropdownWidth = "w-full",
  fitContent = false,
  disabled = false,
  usePortal = true,
  keepOpenOnSelect,
  suspendListboxKeyboard = false,
  isOptionDisabled,
  "data-testid": dataTestId,
}: SelectDropdownProps<T>) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [buttonWidth, setButtonWidth] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [anchorPosition, setAnchorPosition] = useState<
    "bottom start" | "top start" | "bottom end" | "top end"
  >("bottom start");
  const [positionClasses, setPositionClasses] = useState<string>("mt-2");
  const [maxHeight, setMaxHeight] = useState<number | undefined>(384);
  // Track if we should keep open on the next close attempt
  const keepOpenRef = useRef(false);
  const lastPointerTargetRef = useRef<EventTarget | null>(null);
  const usesControlledOpen = Boolean(keepOpenOnSelect || renderListFooter);

  useEffect(() => {
    const trackPointer = (event: MouseEvent) => {
      lastPointerTargetRef.current = event.target;
    };
    document.addEventListener("mousedown", trackPointer, true);
    document.addEventListener("mouseup", trackPointer, true);
    return () => {
      document.removeEventListener("mousedown", trackPointer, true);
      document.removeEventListener("mouseup", trackPointer, true);
    };
  }, []);

  // Headless UI registers bubble-phase key handlers on the listbox panel; capture
  // here so Space/Enter cannot select the focused row while inline fields are active.
  useEffect(() => {
    if (!suspendListboxKeyboard || !isOpen) return;

    const container = buttonRef.current?.closest(".relative");
    const optionsEl = container?.querySelector('[role="listbox"]');
    if (!(optionsEl instanceof HTMLElement)) return;

    optionsEl.addEventListener("keydown", blockListboxSelectionKeys, true);
    return () => {
      optionsEl.removeEventListener("keydown", blockListboxSelectionKeys, true);
    };
  }, [suspendListboxKeyboard, isOpen]);

  // Handle value change with optional keep-open behavior
  const handleChange = (newValue: T) => {
    if (keepOpenOnSelect && keepOpenOnSelect(newValue)) {
      // Set flag before onChange to prevent close
      keepOpenRef.current = true;
      // Also ensure isOpen stays true
      setIsOpen(true);
    }
    onChange(newValue);
  };

  // Handle close with keep-open logic
  const handleClose = () => {
    if (keepOpenRef.current) {
      // Don't close, but reset the flag after a brief delay
      // This allows the state update from handleChange to take effect
      setTimeout(() => {
        keepOpenRef.current = false;
      }, 10);
      setIsOpen(true);
      return;
    }
    // Portaled calendars sit outside the listbox DOM; keep open while interacting
    if (
      renderListFooter &&
      isDatePickerInteractionTarget(lastPointerTargetRef.current, null, null)
    ) {
      setIsOpen(true);
      return;
    }
    if (keepOpenOnSelect?.(value)) {
      if (
        suspendListboxKeyboard ||
        isDatePickerInteractionTarget(lastPointerTargetRef.current, null, null)
      ) {
        setIsOpen(true);
        return;
      }
    }
    setIsOpen(false);
  };

  // Measure button width when it changes
  useEffect(() => {
    if (buttonRef.current && usePortal) {
      const updateWidth = () => {
        const width = buttonRef.current?.offsetWidth || null;
        setButtonWidth(width);
      };

      updateWidth();

      // Update width on resize
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(buttonRef.current);

      return () => resizeObserver.disconnect();
    }
  }, [usePortal]);

  // Calculate dropdown position based on available space
  const calculatePosition = React.useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Estimate dropdown height based on number of options (each option ~40px, plus padding)
    const optionHeight = 40;
    const headerFooterHeight =
      (renderListHeader ? 50 : 0) + (renderListFooter ? 50 : 0);
    const padding = 16; // p-2 = 8px top + 8px bottom
    const estimatedDropdownHeight = Math.min(
      options.length * optionHeight + headerFooterHeight + padding,
      384 // max-h-96
    );
    const gap = 8; // 0.5rem = 8px

    const spaceBelow = viewportHeight - buttonRect.bottom - gap;
    const spaceAbove = buttonRect.top - gap;

    const clip = getHorizontalClipBounds(buttonRef.current);
    const rel = buttonRef.current.closest(".relative");
    const listboxCandidate = rel?.querySelector('[role="listbox"]');
    const listbox =
      listboxCandidate instanceof HTMLElement ? listboxCandidate : null;
    let menuWidth = estimateMenuWidthPx(options, buttonRect.width);
    if (listbox && listbox.offsetWidth > 0) {
      menuWidth = listbox.getBoundingClientRect().width;
    }

    // Prefer anchoring to the right when that keeps more of the menu inside overflow-x clips
    // (e.g. Mission Control: wide `w-max` panel inside an `overflow-hidden` shell).
    const overflowIfLeftAnchored = Math.max(
      0,
      buttonRect.left + menuWidth - clip.right
    );
    const overflowIfRightAnchored = Math.max(
      0,
      clip.left - (buttonRect.right - menuWidth)
    );
    const shouldPositionRight =
      overflowIfLeftAnchored > 0 &&
      overflowIfRightAnchored < overflowIfLeftAnchored;

    // Flip above if there's not enough space below (with buffer for safety)
    // Use a threshold that's less than estimated height to flip earlier
    const flipThreshold = estimatedDropdownHeight * 0.8; // Flip when 80% of estimated height would fit
    const shouldPositionAbove = spaceBelow < flipThreshold && spaceAbove > 100; // Need at least 100px above

    // Calculate max height based on available space
    const availableSpace = shouldPositionAbove ? spaceAbove : spaceBelow;
    const calculatedMaxHeight = Math.min(availableSpace - 10, 384); // 10px buffer
    setMaxHeight(Math.max(calculatedMaxHeight, 200)); // Minimum 200px

    if (usePortal) {
      // Portal mode: use anchor positioning
      if (shouldPositionAbove && shouldPositionRight) {
        setAnchorPosition("top end");
      } else if (shouldPositionAbove) {
        setAnchorPosition("top start");
      } else if (shouldPositionRight) {
        setAnchorPosition("bottom end");
      } else {
        setAnchorPosition("bottom start");
      }
    } else {
      // Non-portal mode: use CSS classes
      const verticalClass = shouldPositionAbove
        ? "bottom-full mb-2"
        : "top-full mt-2";
      const horizontalClass = shouldPositionRight ? "right-0" : "left-0";
      setPositionClasses(`${verticalClass} ${horizontalClass}`);
    }
  }, [usePortal, options, renderListHeader, renderListFooter]);

  // Calculate position proactively on mount and when button dimensions change
  useEffect(() => {
    calculatePosition();
  }, [calculatePosition]);

  // Calculate position when button is clicked (before dropdown opens)
  const handleButtonClick = React.useCallback(() => {
    // Calculate position immediately on click
    calculatePosition();
    if (usesControlledOpen) {
      setIsOpen((open) => !open);
    }
  }, [calculatePosition, usesControlledOpen]);

  // Detect when dropdown opens/closes and recalculate position
  useEffect(() => {
    if (!buttonRef.current) return;

    const container = buttonRef.current.closest(".relative");
    if (!container) return;

    const observer = new MutationObserver(() => {
      // Check for ListboxOptions element
      const dropdown = container.querySelector('[role="listbox"]');
      const isDropdownVisible =
        dropdown &&
        dropdown instanceof HTMLElement &&
        !dropdown.hasAttribute("data-closed") &&
        dropdown.offsetParent !== null;

      if (isDropdownVisible && !isOpen) {
        setIsOpen(true);
        // Recalculate position after dropdown is visible to get accurate measurements
        setTimeout(calculatePosition, 10);
      } else if (!isDropdownVisible && isOpen) {
        if (keepOpenRef.current || keepOpenOnSelect?.(value)) {
          setIsOpen(true);
          return;
        }
        setIsOpen(false);
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-closed", "style"],
    });

    return () => observer.disconnect();
  }, [isOpen, calculatePosition, keepOpenOnSelect, value]);

  // Recalculate on scroll and resize when open
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("scroll", calculatePosition, true);
    window.addEventListener("resize", calculatePosition);

    return () => {
      window.removeEventListener("scroll", calculatePosition, true);
      window.removeEventListener("resize", calculatePosition);
    };
  }, [isOpen, calculatePosition]);

  // Find the selected option object
  const selectedOption =
    options.find((option) => option.value === value) || options[0] || null;

  const fitContentWidthPx = fitContent
    ? estimateMenuWidthPx(options as SelectOption<unknown>[], 0)
    : null;

  const portalMenuWidth =
    usePortal
      ? estimateMenuWidthPx(options, buttonWidth ?? 0)
      : null;

  const resolvedPortalMenuWidth =
    portalMenuWidth ??
    (usePortal ? estimateMenuWidthPx(options, 0) : null);

  return (
    <div className={clsx(className, inline && "flex items-center gap-1.5")}>
      {label && (
        <label
          className={clsx(
            SELECT_LABEL_CLASS,
            disabled && "text-slate-400 dark:text-slate-500",
            inline ? "mb-0 whitespace-nowrap pl-0 pr-1" : "mb-4 px-2",
          )}
        >
          {label}
        </label>
      )}
      <Listbox
        value={value}
        onChange={handleChange}
        disabled={disabled}
        {...(usesControlledOpen ? { open: isOpen, onClose: handleClose } : {})}
      >
        <div
          className={clsx(
            "relative",
            inline ? "shrink-0" : buttonContainerClassName ?? "mt-1.5",
          )}
          style={
            fitContentWidthPx
              ? { minWidth: `${fitContentWidthPx}px` }
              : undefined
          }
        >
          <ListboxButton
            ref={buttonRef}
            onClick={handleButtonClick}
            data-testid={dataTestId}
            className={clsx(SELECT_BUTTON_SURFACE_CLASS, buttonClassName)}
          >
            <span
              className={clsx(
                "block pr-2",
                fitContent ? "whitespace-nowrap" : "truncate",
              )}
            >
              {renderSelectedItem
                ? renderSelectedItem(
                    selectedOption ?? { value, label: "" },
                  )
                : selectedOption?.label}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <FontAwesomeIcon
                icon={faChevronDown}
                aria-hidden="true"
                className="size-4 text-slate-400 dark:text-slate-500 sm:size-4"
              />
            </span>
          </ListboxButton>

          {usePortal ? (
              <ListboxOptions
                portal
                anchor={anchorPosition}
                transition
                onKeyDown={handleListboxEditableKeyDown}
                className={clsx(
                  "z-[9999] [--anchor-gap:0.5rem] [--anchor-padding:1rem]",
                  LISTBOX_PANEL_CLASS,
                  fitContent
                    ? "w-max max-w-md min-w-[var(--button-width)]"
                    : resolvedPortalMenuWidth
                      ? ""
                      : "min-w-[200px] max-w-xs",
                  dropdownClassName,
                )}
                style={
                  fitContent
                    ? undefined
                    : {
                        width: resolvedPortalMenuWidth
                          ? `${resolvedPortalMenuWidth}px`
                          : buttonWidth
                            ? `${buttonWidth}px`
                            : undefined,
                        minWidth: resolvedPortalMenuWidth
                          ? `${resolvedPortalMenuWidth}px`
                          : buttonWidth
                            ? `${buttonWidth}px`
                            : undefined,
                        maxWidth: resolvedPortalMenuWidth
                          ? `${DROPDOWN_MAX_WIDTH_PX}px`
                          : undefined,
                      }
                }
              >
                <div
                  className="flex flex-col"
                  style={{ maxHeight: `${maxHeight}px` }}
                >
                  {renderListHeader && (
                    <div className="flex-shrink-0">{renderListHeader()}</div>
                  )}

                  <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
                    {options.map((option, index) => (
                      <React.Fragment
                        key={`${
                          typeof option.value === "object"
                            ? JSON.stringify(option.value)
                            : String(option.value)
                        }-${index}`}
                      >
                        {renderBeforeOption?.(option, index, options)}
                        <ListboxOption
                          value={option.value}
                          disabled={isOptionDisabled?.(option.value) ?? false}
                          className={clsx(
                            LISTBOX_OPTION_CLASS,
                            fitContent ? "overflow-visible" : "overflow-hidden",
                          )}
                        >
                          {({ selected, focus }) => (
                            <>
                              {renderListItem ? (
                                <div
                                  className={
                                    focus ? "bg-indigo-500 text-white" : ""
                                  }
                                >
                                  {renderListItem(option, selected, focus)}
                                </div>
                              ) : (
                              <div className="py-2 pr-10 pl-3">
                                <span
                                  className={clsx(
                                    "block font-normal group-data-selected:font-semibold",
                                    fitContent ? "whitespace-nowrap" : "truncate",
                                  )}
                                >
                                  {option.label}
                                </span>
                                <span
                                  className={clsx(
                                    "absolute inset-y-0 right-0 flex items-center pr-3",
                                    "text-indigo-600 dark:text-indigo-400",
                                    "group-not-data-selected:hidden",
                                    "group-data-focus:text-indigo-800 dark:group-data-focus:text-white",
                                  )}
                                >
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    aria-hidden="true"
                                    className="size-4"
                                  />
                                </span>
                              </div>
                              )}
                            </>
                          )}
                        </ListboxOption>
                      </React.Fragment>
                    ))}
                  </div>

                  {renderListFooter && (
                    <div
                      className="flex-shrink-0 overflow-visible"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {renderListFooter()}
                    </div>
                  )}
                </div>
              </ListboxOptions>
          ) : (
            <ListboxOptions
              transition
              onKeyDown={handleListboxEditableKeyDown}
              className={clsx(
                "absolute z-50",
                positionClasses,
                fitContent ? "w-max" : dropdownWidth,
                LISTBOX_PANEL_CLASS,
                dropdownClassName,
              )}
              style={
                fitContentWidthPx
                  ? {
                      minWidth: `${fitContentWidthPx}px`,
                      width: "max-content",
                      maxWidth: `${DROPDOWN_MAX_WIDTH_PX}px`,
                    }
                  : undefined
              }
            >
              <div className="flex flex-col max-h-96">
                {renderListHeader && (
                  <div className="flex-shrink-0">{renderListHeader()}</div>
                )}

                <div className="min-h-0 flex-1 space-y-1 overflow-y-auto">
                  {options.map((option, index) => (
                    <React.Fragment
                      key={`${
                        typeof option.value === "object"
                          ? JSON.stringify(option.value)
                          : String(option.value)
                      }-${index}`}
                    >
                      {renderBeforeOption?.(option, index, options)}
                      <ListboxOption
                        value={option.value}
                        disabled={isOptionDisabled?.(option.value) ?? false}
                        className={clsx(
                          LISTBOX_OPTION_CLASS,
                          fitContent ? "overflow-visible" : "overflow-hidden",
                        )}
                      >
                        {({ selected, focus }) => (
                          <>
                            {renderListItem ? (
                              <div
                                className={
                                  focus ? "bg-indigo-500 text-white" : ""
                                }
                              >
                                {renderListItem(option, selected, focus)}
                              </div>
                            ) : (
                              <div className="py-2 pr-10 pl-3">
                                <span
                                  className={clsx(
                                    "block font-normal group-data-selected:font-semibold",
                                    fitContent ? "whitespace-nowrap" : "truncate",
                                  )}
                                >
                                {option.label}
                              </span>
                              <span
                                className={clsx(
                                  "absolute inset-y-0 right-0 flex items-center pr-3",
                                  "text-indigo-600 dark:text-indigo-400",
                                  "group-not-data-selected:hidden",
                                  "group-data-focus:text-indigo-800 dark:group-data-focus:text-white",
                                )}
                              >
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  aria-hidden="true"
                                  className="size-4"
                                />
                              </span>
                            </div>
                            )}
                          </>
                        )}
                      </ListboxOption>
                    </React.Fragment>
                  ))}
                </div>

                {renderListFooter && (
                  <div
                    className="flex-shrink-0 overflow-visible"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    {renderListFooter()}
                  </div>
                )}
              </div>
            </ListboxOptions>
          )}
        </div>
      </Listbox>
    </div>
  );
}
